import React, { useState, useEffect, memo } from "react";
import GoogleMapReact from "google-map-react";
import { Card, Button, Input, Spin, Row, Checkbox, message } from "antd";
import useFirmService from "../../../../services/useFirmService";

// Memoize the markers to prevent unnecessary re-renders
const SelectedLocationMarker = memo(({ lat, lng }) => {
  // Use $hover to prevent the div from being recreated during hover
  return (
    <div
      style={{
        position: "absolute",
        transform: "translate(-50%, -100%)",
        cursor: "pointer",
        willChange: "transform", // Optimize for transforms
      }}>
      <div style={{ position: "relative", width: "40px", height: "40px" }}>
        <div
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            background: "white",
            borderRadius: "50% 50% 50% 0",
            transform: "rotate(-45deg)",
            boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: "80%",
            height: "80%",
            top: "10%",
            left: "10%",
            borderRadius: "50%",
            overflow: "hidden",
            transform: "rotate(45deg)",
          }}>
          <img
            src="/hemen_personel_icon.png"
            alt="location marker"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transform: "rotate(-45deg)",
            }}
          />
        </div>
      </div>
    </div>
  );
});

const CurrentLocationMarker = memo(() => (
  <div
    style={{
      width: "20px",
      height: "20px",
      backgroundColor: "#4285F4",
      border: "2px solid white",
      borderRadius: "50%",
      transform: "translate(-50%, -50%)",
      willChange: "transform",
    }}
  />
));

const LocationPicker = ({
  value = {},
  onChange,
  saveLocation,
  setSaveLocation,
  initialLocation = null,
  isAdvertCreateOrEdit = true,
}) => {
  const { useGetGeoCodingAdress } = useFirmService();
  const { mutateAsync: getAdress, isPending: isAddressLoading } =
    useGetGeoCodingAdress();
  const [currentLocation, setCurrentLocation] = useState(null);
  const [map, setMap] = useState(null);
  const [maps, setMaps] = useState(null);

  // Get selected coordinates with fixed precision immediately
  const selectedLat = value?.latitude || initialLocation?.latitude;
  const selectedLng = value?.longitude || initialLocation?.longitude;
  const fixedLat = selectedLat ? Number(selectedLat.toFixed(6)) : null;
  const fixedLng = selectedLng ? Number(selectedLng.toFixed(6)) : null;

  const defaultCenter = {
    lat: fixedLat || 41.015137,
    lng: fixedLng || 28.97953,
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: Number(position.coords.latitude.toFixed(6)),
            lng: Number(position.coords.longitude.toFixed(6)),
          };
          setCurrentLocation(pos);
        },
        handleLocationError,
        { enableHighAccuracy: true }
      );
    }
  }, []);

  const handleLocationError = (error) => {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        message.error(
          "Konum izni reddedildi. Lütfen konum erişimini etkinleştirin."
        );
        break;
      case error.POSITION_UNAVAILABLE:
        message.error("Konum bilgisi kullanılamıyor.");
        break;
      case error.TIMEOUT:
        message.error("Konum isteği zaman aşımına uğradı.");
        break;
      default:
        message.error("Konum alınırken bir hata oluştu.");
    }
  };

  const handleApiLoaded = ({ map: googleMap, maps: googleMaps }) => {
    setMap(googleMap);
    setMaps(googleMaps);
  };

  const handleMapClick = ({ lat, lng }) => {
    // Fix precision immediately on click
    onChange({
      latitude: Number(lat.toFixed(6)),
      longitude: Number(lng.toFixed(6)),
      address: value?.address || "",
    });
  };

  const handleGetAddress = async () => {
    if (fixedLat && fixedLng) {
      try {
        const response = await getAdress({
          latitude: fixedLat,
          longitude: fixedLng,
        });
        onChange({ ...value, address: response.data.address });
      } catch (error) {
        console.error("Adres alınırken hata oluştu:", error);
      }
    }
  };

  return (
    <Card>
      <Spin spinning={isAddressLoading}>
        <div style={{ height: "400px", width: "100%", marginBottom: "8px" }}>
          <GoogleMapReact
            bootstrapURLKeys={{
              key: process.env.REACT_APP_GOOGLE_MAPS_KEY,
              libraries: ["places"],
            }}
            defaultCenter={defaultCenter}
            defaultZoom={13}
            onClick={handleMapClick}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={handleApiLoaded}
            options={{
              fullscreenControl: false,
              clickableIcons: false,
              zoomControl: true,
              scaleControl: true,
              streetViewControl: false,
              rotateControl: true,
            }}>
            {currentLocation && (
              <CurrentLocationMarker
                lat={currentLocation.lat}
                lng={currentLocation.lng}
              />
            )}
            {fixedLat && fixedLng && (
              <SelectedLocationMarker lat={fixedLat} lng={fixedLng} />
            )}
          </GoogleMapReact>
        </div>
        {fixedLat && fixedLng && (
          <>
            <div
              style={{
                marginTop: 8,
                fontSize: 14,
                color: "rgba(0, 0, 0, 0.45)",
              }}>
              Seçilen Konum: {fixedLat}, {fixedLng}
            </div>
            <Row justify="center">
              <Button
                type="primary"
                onClick={handleGetAddress}
                style={{ margin: 8, height: 40, width: "35%" }}>
                Adresi Getir
              </Button>
            </Row>
          </>
        )}
        <Input.TextArea
          style={{ marginTop: 8, marginBottom: 10 }}
          value={value?.address || initialLocation?.address || ""}
          onChange={(e) => onChange({ ...value, address: e.target.value })}
          rows={4}
        />
        <div style={{ marginBottom: 10 }}>
          Adreste hata olduğunu düşünüyorsanız düzenleyebilirsiniz!
        </div>
        {isAdvertCreateOrEdit && (
          <Checkbox
            checked={saveLocation}
            onChange={(e) => setSaveLocation(e.target.checked)}>
            Adresi sonraki ilanlarımda kullanmak için kaydet
          </Checkbox>
        )}
      </Spin>
    </Card>
  );
};

export default LocationPicker;
